import React from "react";
import { Section } from "@atoms/Section";
import color from "@definitions/mui/color";
import Text from "@ds/components/Text";
import { useTranslation } from "next-i18next";
import Box from "@mui/material/Box";
import { Carousel } from "@atoms/Carousel";
import useAction from "@containers/About/hooks/useAction";
import useFilterCollectionByLang from "@utils/useFilterCollectionByLang";
import {
  ILandingTrustedProps,
  ITestimony,
  ITestimonyLocalization,
  ITrustItemProps,
} from "@containers/LandingPage/libs/MobilePwa/Mobile.types";
import DynamicImage from "@ds/components/DynamicImage";

const TrustItem: React.FC<ITrustItemProps> = ({ title, label, icon }) => {
  return (
    <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
      <Box
        width="48px"
        height="48px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        bgcolor="#E2E4E5"
        borderRadius="50%"
      >
        <DynamicImage src={icon} alt={label} width={22} height={22} />
      </Box>
      <Text
        weight="700"
        variant="h3"
        sx={{
          marginTop: "12px",
          marginBottom: "4px",
          display: "block",
          lineHeight: "26px",
        }}
      >
        {label}
      </Text>
      <Text
        weight="600"
        variant="h4"
        sx={{
          fontSize: "14px",
          display: "block",
          lineHeight: "20px",
        }}
      >
        {title}
      </Text>
    </Box>
  );
};

const LandingTrusted: React.FC<ILandingTrustedProps> = ({ testimony }) => {
  const { t } = useTranslation("common");
  const { corporateReview } = useAction();
  const { filteredCollection } = useFilterCollectionByLang<ITestimony, ITestimonyLocalization>(
    testimony?.testimonies,
  );

  return (
    <Section maxWidth="full-width" py="32px" px="16px">
      <Text
        weight="700"
        variant="h3"
        align="center"
        sx={{
          margin: "auto",
          maxWidth: "214px",
          color: color.general.main,
          lineHeight: "26px",
          display: "block",
        }}
      >
        {t("trustedSection.title")}
      </Text>
      <Box
        py="24px"
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        gap="16px"
        sx={{
          "& svg": {
            width: "48px",
            height: "48px",
          },
        }}
      >
        {corporateReview.map((item, idx: number) => (
          <TrustItem key={idx} title={item.title} label={item.point} icon={item.image} />
        ))}
      </Box>
      <Box
        sx={{
          "& .slick-dots": {
            bottom: "18px",
            transition: "all 300ms",
            "& li": {
              margin: "0 6px",
            },
          },
        }}
      >
        <Carousel settings={{ dots: true }} arrows={false}>
          {filteredCollection.map((item, i: number) => {
            return (
              <Box key={i} px="8px">
                <Box
                  px="24px"
                  py="32px"
                  pb="54px"
                  sx={{
                    border: `1px solid ${color.grey.light}`,
                    borderRadius: "8px",
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <DynamicImage
                      alt="testimony"
                      src={item.image.mediaPath}
                      width={40}
                      height={40}
                      blurDataURL={item.image.mediaPath}
                      placeholder="blur"
                    />
                    <Box ml="12px">
                      <Text
                        weight="700"
                        variant="h3"
                        sx={{
                          fontSize: "16px",
                          lineHeight: "16px",
                          color: color.general.main,
                        }}
                      >
                        {item.localization?.name}
                      </Text>
                      <Text
                        weight="400"
                        variant="body2"
                        sx={{
                          marginTop: 4,
                          lineHeight: "19px",
                          color: color.inactive.secondary,
                          display: "block",
                        }}
                      >
                        {item.localization?.jobTitle}
                      </Text>
                    </Box>
                  </Box>
                  <Text
                    component="p"
                    weight="600"
                    variant="body2"
                    sx={{
                      marginTop: 16,
                      lineHeight: "19px",
                    }}
                  >
                    {item.localization?.description}
                  </Text>
                </Box>
              </Box>
            );
          })}
        </Carousel>
      </Box>
    </Section>
  );
};

export default LandingTrusted;
